import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Command } from "cmdk";
import { Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useAuth } from "@/contexts/AuthContext";
import {
  ACTION_ENTRIES,
  NAV_ENTRIES,
  entryMatches,
  type PaletteEntry,
} from "@/components/admin/command/palette-data";
import { PaletteGroup, PaletteRow } from "@/components/admin/command/PaletteRow";
import { EntitySearchResults } from "@/components/admin/command/SearchResults";

// ============================================================
// Command Palette globale (⌘K / Ctrl+K): navigazione, azioni
// rapide e ricerca live su clienti/progetti/task/preventivi/
// referenti. Filtro manuale (accent-insensitive) al posto di
// quello interno di cmdk, così pagine e dati seguono la stessa
// logica di match.
// ============================================================

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const { isSuperAdmin, canAccess } = useAuth();

  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    }
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (!next) setQuery("");
  }

  function go(path: string) {
    navigate(path);
    handleOpenChange(false);
  }

  const visible = (entry: PaletteEntry) =>
    (!entry.superAdminOnly || isSuperAdmin) &&
    canAccess(entry.module) &&
    entryMatches(entry, query);

  const navEntries = NAV_ENTRIES.filter(visible);
  const actionEntries = ACTION_ENTRIES.filter(visible);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/75 backdrop-blur-md data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0" />
        <DialogPrimitive.Content className="fixed left-1/2 top-[18%] z-50 w-[calc(100vw-2rem)] max-w-xl -translate-x-1/2 outline-none">
          <DialogPrimitive.Title className="sr-only">Command palette</DialogPrimitive.Title>
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ type: "spring", stiffness: 380, damping: 28 }}
            className="overflow-hidden rounded-2xl border border-primary/25 bg-card shadow-renaissance ring-1 ring-primary/10"
          >
            {/* Filo d'oro in cima al pannello */}
            <div className="h-px bg-gradient-to-r from-transparent via-primary/60 to-transparent" />

            <Command label="Comandi" shouldFilter={false} className="flex flex-col">
              <div className="flex items-center gap-2.5 border-b border-primary/15 px-4">
                <Sparkles className="h-4 w-4 shrink-0 text-primary" />
                <Command.Input
                  value={query}
                  onValueChange={setQuery}
                  placeholder="Cerca pagine, clienti, task, preventivi…"
                  className="h-12 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                />
                <Kbd>esc</Kbd>
              </div>

              <Command.List className="max-h-[60vh] overflow-y-auto p-2">
                <Command.Empty className="py-10 text-center text-sm text-muted-foreground">
                  Nessun risultato per{" "}
                  <span className="font-medium text-primary">“{query}”</span>.
                </Command.Empty>

                {/* Risultati live: clienti, progetti, task, preventivi, referenti */}
                <EntitySearchResults query={query} onNavigate={go} />

                {navEntries.length > 0 && (
                  <PaletteGroup heading="Naviga">
                    {navEntries.map((e) => (
                      <PaletteRow
                        key={`nav:${e.path}`}
                        value={`nav:${e.path}`}
                        icon={e.icon}
                        label={e.label}
                        hint={e.hint}
                        onSelect={() => go(e.path)}
                      />
                    ))}
                  </PaletteGroup>
                )}

                {actionEntries.length > 0 && (
                  <PaletteGroup heading="Azioni">
                    {actionEntries.map((e) => (
                      <PaletteRow
                        key={`action:${e.path}:${e.label}`}
                        value={`action:${e.path}:${e.label}`}
                        icon={e.icon}
                        label={e.label}
                        hint={e.hint}
                        onSelect={() => go(e.path)}
                      />
                    ))}
                  </PaletteGroup>
                )}
              </Command.List>

              <div className="flex items-center justify-between border-t border-primary/15 px-3 py-2 text-[10px] text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Kbd>↑</Kbd>
                  <Kbd>↓</Kbd> naviga
                </span>
                <span className="flex items-center gap-1">
                  <Kbd>⏎</Kbd> apri
                </span>
                <span className="flex items-center gap-1">
                  <Kbd>esc</Kbd> chiudi
                </span>
                <span className="flex items-center gap-1">
                  <Kbd>⌘</Kbd>
                  <Kbd>K</Kbd> toggle
                </span>
              </div>
            </Command>
          </motion.div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="rounded border border-primary/20 bg-primary/5 px-1.5 py-0.5 font-mono text-[10px] text-primary/80">
      {children}
    </kbd>
  );
}
